<!--
  Fornitori ordini list — redesigned 2026-06-09 (v2.5.0).

  Previous markup was kendo-grid (10 columns conditional on
  view='lavorazione'|'storico') + kendo-datepicker (only kendo
  dependency in TS). TS was already modern (router, filtering,
  view toggle). HTML+SCSS rewrite drops kendo-grid + kendo-datepicker
  and aligns to the Diakonix family (suppliers role = $warn amber).

  Sub-routes `/fornitori-ordini/dettagli/:...` and `/storico/:...`
  are still rendered via the trailing `<router-outlet>`.
-->

<div class="orders-page">
  <!-- ─── Sticky header + filters ─────────────────────────────── -->
  <div class="orders-sticky-top">
    <header class="orders-header">
      <div class="orders-header__titles">
        <h1 class="orders-header__title">📦 Ordini al fornitore</h1>
        <p class="orders-header__subtitle">
          @if (orders.length > 0) {
            <strong>{{ orders.length }}</strong>
            {{
              orders.length === 1
                ? "ordine al fornitore"
                : "ordini al fornitore"
            }}
          } @else if (loading) {
            Caricamento…
          } @else {
            Nessun ordine registrato
          }
        </p>
      </div>

      <div class="orders-header__controls">
        <!-- View toggle (Lavorazione / Storico) -->
        <div class="view-toggle" role="tablist" aria-label="Vista ordini">
          <button
            type="button"
            class="view-toggle__btn"
            [class.view-toggle__btn--active]="view === 'lavorazione'"
            (click)="setView('lavorazione')"
            role="tab"
          >
            🔄 In lavorazione
          </button>
          <button
            type="button"
            class="view-toggle__btn"
            [class.view-toggle__btn--active]="view === 'storico'"
            (click)="setView('storico')"
            role="tab"
          >
            📜 Storico
          </button>
        </div>

        <button
          type="button"
          class="refresh-btn"
          (click)="reload()"
          title="Aggiorna"
          aria-label="Aggiorna lista"
        >
          🔁
        </button>
      </div>
    </header>

    <!-- ─── Filter bar ───────────────────────────────────────────── -->
    <section class="orders-filters" aria-label="Filtri ricerca">
      <div class="filter-field">
        <label class="filter-field__label" for="searchOrderNumber"
          >N° ordine</label
        >
        <div class="filter-field__input-wrap">
          <input
            id="searchOrderNumber"
            type="search"
            class="filter-field__input"
            placeholder="Cerca n° ordine…"
            [(ngModel)]="searchOrderNumber"
            (ngModelChange)="applyView()"
          />
          @if (searchOrderNumber.trim()) {
            <button
              type="button"
              class="filter-field__clear"
              (click)="searchOrderNumber = ''; applyView()"
              aria-label="Rimuovi filtro n° ordine"
            >
              ×
            </button>
          }
        </div>
      </div>

      <div class="filter-field">
        <label class="filter-field__label" for="searchFornitore"
          >Fornitore</label
        >
        <div class="filter-field__input-wrap">
          <input
            id="searchFornitore"
            type="search"
            class="filter-field__input"
            placeholder="Cerca nome o codice fornitore…"
            [(ngModel)]="searchFornitore"
            (ngModelChange)="applyView()"
          />
          @if (searchFornitore.trim()) {
            <button
              type="button"
              class="filter-field__clear"
              (click)="searchFornitore = ''; applyView()"
              aria-label="Rimuovi filtro fornitore"
            >
              ×
            </button>
          }
        </div>
      </div>

      <div class="filter-field filter-field--dates">
        <label class="filter-field__label">Data ordine</label>
        <div class="filter-field__date-pair">
          <input
            type="date"
            class="filter-field__input filter-field__input--date"
            [(ngModel)]="startDateStr"
            (ngModelChange)="applyView()"
            aria-label="Data da"
          />
          <span class="filter-field__date-sep">→</span>
          <input
            type="date"
            class="filter-field__input filter-field__input--date"
            [(ngModel)]="endDateStr"
            (ngModelChange)="applyView()"
            aria-label="Data a"
          />
          @if (startDate || endDate) {
            <button
              type="button"
              class="filter-field__clear"
              (click)="startDate = null; endDate = null; applyView()"
              aria-label="Rimuovi filtri data"
            >
              ×
            </button>
          }
        </div>
      </div>

      @if (filtersActive) {
        <button
          type="button"
          class="filters-clear-all"
          (click)="clearFilters()"
          title="Pulisci tutti i filtri"
        >
          Pulisci filtri
        </button>
      }
    </section>
  </div>
  <!-- /.orders-sticky-top -->

  <!-- ─── Table card ───────────────────────────────────────────── -->
  <section class="orders-table-card" aria-label="Elenco ordini fornitore">
    @if (loading) {
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7, 8]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__line table-skeleton__line--lg"></div>
            <div class="table-skeleton__line table-skeleton__line--md"></div>
            <div class="table-skeleton__line table-skeleton__line--sm"></div>
          </div>
        }
      </div>
    } @else if (orders.length === 0) {
      <div class="orders-empty">
        <div class="orders-empty__icon" aria-hidden="true">
          @if (filtersActive) {
            🔎
          } @else {
            📭
          }
        </div>
        <p class="orders-empty__msg">
          @if (filtersActive) {
            Nessun ordine corrisponde ai filtri attuali.
          } @else if (view === "lavorazione") {
            Nessun ordine in lavorazione al momento.
          } @else {
            Nessun ordine nello storico.
          }
        </p>
      </div>
    } @else {
      <div class="orders-table-scroll">
        <table class="orders-table" role="table">
          <thead>
            <tr>
              <th class="col-order" (click)="sortBy('numero_ordine')">
                N° ordine
                @if (sortColumn === "numero_ordine") {
                  <span>
                    {{ sortDirection === "asc" ? "▲" : "▼" }}
                  </span>
                }
              </th>
              <th class="col-supplier" (click)="sortBy('or_fornitore')">
                Fornitore
                @if (sortColumn === "or_fornitore") {
                  <span>
                    {{ sortDirection === "asc" ? "▲" : "▼" }}
                  </span>
                }
              </th>
              <th
                class="col-supplier-code"
                (click)="sortBy('or_codice_fornitore')"
              >
                Cod. fornitore
                @if (sortColumn === "or_codice_fornitore") {
                  <span>
                    {{ sortDirection === "asc" ? "▲" : "▼" }}
                  </span>
                }
              </th>
              <th class="col-date" (click)="sortBy('or_data_ordine')">
                Data ordine
                @if (sortColumn === "or_data_ordine") {
                  <span>
                    {{ sortDirection === "asc" ? "▲" : "▼" }}
                  </span>
                }
              </th>
              <th class="col-date" (click)="sortBy('or_data_assegnazione')">
                Data prev. evas.
                @if (sortColumn === "or_data_assegnazione") {
                  <span>
                    {{ sortDirection === "asc" ? "▲" : "▼" }}
                  </span>
                }
              </th>
              @if (view === "lavorazione") {
                <th class="col-date" (click)="sortBy('or_data_arrivo')">
                  Data arrivo
                  @if (sortColumn === "or_data_arrivo") {
                    <span>
                      {{ sortDirection === "asc" ? "▲" : "▼" }}
                    </span>
                  }
                </th>
              } @else {
                <th class="col-date" (click)="sortBy('or_data_chiusura')">
                  Data chiusura
                  @if (sortColumn === "or_data_chiusura") {
                    <span>
                      {{ sortDirection === "asc" ? "▲" : "▼" }}
                    </span>
                  }
                </th>
              }
              <th class="col-num" (click)="sortBy('num_prodotti')">
                N° articoli
                @if (sortColumn === "num_prodotti") {
                  <span>
                    {{ sortDirection === "asc" ? "▲" : "▼" }}
                  </span>
                }
              </th>
              <th class="col-num" (click)="sortBy('num_pezzi')">
                N° pezzi
                @if (sortColumn === "num_pezzi") {
                  <span>
                    {{ sortDirection === "asc" ? "▲" : "▼" }}
                  </span>
                }
              </th>
              <th class="col-progress" (click)="sortBy('ultimo_stato_perc')">
                Avanzamento
                @if (sortColumn === "ultimo_stato_perc") {
                  <span>
                    {{ sortDirection === "asc" ? "▲" : "▼" }}
                  </span>
                }
              </th>
              <th class="col-sollecito" (click)="sortBy('sollecito')">
                Sollecito
                @if (sortColumn === "sollecito") {
                  <span>
                    {{ sortDirection === "asc" ? "▲" : "▼" }}
                  </span>
                }
              </th>
              <th class="col-actions" aria-label="Azioni"></th>
            </tr>
          </thead>
          <tbody>
            @for (
              o of orders | sortBy: sortField : sortDir;
              track o.ordine_passivo_id ?? $index
            ) {
              <tr
                class="orders-row"
                (click)="openDetail(o)"
                title="Apri dettagli ordine"
              >
                <td class="col-order">
                  <span class="cell-order-num">{{
                    o.numero_ordine || o.or_numero_ordine || "—"
                  }}</span>
                </td>
                <td class="col-supplier">
                  <span class="cell-supplier">{{ o.or_fornitore || "—" }}</span>
                </td>
                <td class="col-supplier-code">
                  <span class="cell-supplier-code">{{
                    o.or_codice_fornitore || "—"
                  }}</span>
                </td>
                <td class="col-date">
                  <span class="cell-date">{{
                    o.or_data_ordine
                      ? (o.or_data_ordine | date: "dd/MM/yy")
                      : "—"
                  }}</span>
                </td>
                <td class="col-date">
                  <span class="cell-date">{{
                    o.or_data_assegnazione
                      ? (o.or_data_assegnazione | date: "dd/MM/yy")
                      : "—"
                  }}</span>
                </td>
                @if (view === "lavorazione") {
                  <td class="col-date">
                    <span class="cell-date">{{
                      o.or_data_arrivo
                        ? (o.or_data_arrivo | date: "dd/MM/yy")
                        : "—"
                    }}</span>
                  </td>
                } @else {
                  <td class="col-date">
                    <span class="cell-date cell-date--closed">{{
                      o.or_data_chiusura
                        ? (o.or_data_chiusura | date: "dd/MM/yy")
                        : "—"
                    }}</span>
                  </td>
                }
                <td class="col-num">
                  <span class="cell-num">{{ o.num_prodotti ?? 0 }}</span>
                </td>
                <td class="col-num">
                  <span class="cell-num">{{ o.num_pezzi ?? 0 }}</span>
                </td>
                <td class="col-progress">
                  <div class="progress-cell">
                    <div class="progress-cell__bar">
                      <div
                        class="progress-cell__fill"
                        [style.width.%]="
                          view === 'storico' ? 100 : (o.ultimo_stato_perc ?? 0)
                        "
                        [attr.data-state]="progressState(o)"
                      ></div>
                    </div>
                    <span class="progress-cell__pct">
                      {{
                        view === "storico" ? 100 : (o.ultimo_stato_perc ?? 0)
                      }}%
                    </span>
                  </div>
                </td>
                <td class="col-sollecito">
                  @if (o.sollecito) {
                    <span
                      class="sollecito-pill sollecito-pill--yes"
                      title="Sollecitato"
                      >✓</span
                    >
                  } @else {
                    <span
                      class="sollecito-pill sollecito-pill--no"
                      title="Non sollecitato"
                      >—</span
                    >
                  }
                </td>
                <td class="col-actions" (click)="$event.stopPropagation()">
                  <button
                    type="button"
                    class="icon-btn"
                    title="Dettagli ordine"
                    (click)="openDetail(o)"
                  >
                    ℹ️
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>
</div>

<!--
  Child routes (`/fornitori-ordini/dettagli/:...` and
  `/storico/:...`) render the FornitoriOrdiniDettagliComponent
  here. Kept outside the `.orders-page` wrapper so the detail page
  can own its own layout / scroll without inheriting the supplier-list
  page's scrolling container. (Same reason the modals were moved out
  of `.suppliers-page` in fornitore-list-accounts — see the comment
  in that template for the full diagnosis.)
-->
<router-outlet></router-outlet>
